import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { runGated } from "@/lib/adsGate";
import { toast } from "sonner";
import { CalendarClock, Loader2, Sparkles, Trash2, Check, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useLatestBeautyScan } from "./useLatestBeautyScan";
import { ScanRequiredEmpty } from "./beautyUi";
import { syncBeautyReminders } from "@/lib/native";

/**
 * Reads the latest care routine to render suggested reminders, and lets the
 * user opt into each. Enabled reminders show up on /calendar as unified feed
 * items (kind='beauty').
 */
export default function BeautyRoutine() {
  const { scan, loading: scanLoading } = useLatestBeautyScan();
  const [routine, setRoutine] = useState(null);
  const [genLoading, setGenLoading] = useState(false);
  const [enabled, setEnabled] = useState([]);
  const [enabledLoading, setEnabledLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!scan) return;
    // Load existing enabled reminders
    (async () => {
      try {
        const { data } = await api.get("/beauty/reminders");
        const list = data.reminders || [];
        setEnabled(list);
        // Native only: mirror enabled reminders as OS local notifications.
        syncBeautyReminders(list).catch(() => {});
      } finally { setEnabledLoading(false); }
    })();
  }, [scan]);

  const generate = async () => {
    setGenLoading(true);
    try {
      const { data } = await runGated("care_gen", () => api.post("/beauty/care/routine", { focus: "all", scan_id: scan?.id }));
      setRoutine(data.result);
    } catch (e) {
      toast.error("Couldn't build reminder suggestions.");
    } finally { setGenLoading(false); }
  };

  const isEnabled = (type, cadence) =>
    enabled.some(e => e.type === type && e.cadence === cadence && e.enabled);

  const enable = async (r) => {
    setSaving(true);
    try {
      const { data } = await api.post("/beauty/reminders", { reminders: [r] });
      toast.success(`Reminder saved · ${r.type}`);
      let next;
      setEnabled((prev) => {
        const others = prev.filter(x => !(x.type === r.type && x.cadence === r.cadence));
        next = [...others, ...(data.saved || [])];
        return next;
      });
      syncBeautyReminders(next).catch(() => {});
    } finally { setSaving(false); }
  };

  const disable = async (rid) => {
    setSaving(true);
    try {
      await api.delete(`/beauty/reminders/${rid}`);
      let next;
      setEnabled((prev) => {
        next = prev.filter(x => x.id !== rid);
        return next;
      });
      syncBeautyReminders(next).catch(() => {});
    } finally { setSaving(false); }
  };

  if (scanLoading) return <div className="max-w-5xl mx-auto p-16 text-center"><Loader2 className="mx-auto animate-spin" /></div>;
  if (!scan) return <div className="max-w-5xl mx-auto px-6 lg:px-10 py-10"><ScanRequiredEmpty /></div>;

  const suggested = routine?.reminders || [];

  return (
    <div className="max-w-5xl mx-auto px-6 lg:px-10 py-10 lg:py-14" data-testid="beauty-routine-page">
      <p className="eyebrow flex items-center gap-2"><CalendarClock size={12} /> Routine Planner</p>
      <h1 className="editorial-h1 mt-3">Habits that <em>stick.</em></h1>
      <p className="text-sm text-muted-foreground mt-3 max-w-2xl">
        Enable reminders below — they&apos;ll show up on your Stylebay Calendar alongside your outfit events.
      </p>

      <div className="mt-8 grid lg:grid-cols-[1fr,1.4fr] gap-8">
        {/* Generate */}
        <section className="border border-border p-6 self-start">
          <p className="eyebrow">Fresh suggestions</p>
          <p className="text-xs text-muted-foreground mt-2">
            We&apos;ll pull reminder ideas from your latest AI Scan — SPF, cleansing, hair oil, and more.
          </p>
          <Button
            disabled={genLoading}
            onClick={generate}
            className="w-full mt-5 rounded-none uppercase tracking-[0.2em] text-xs h-11"
            data-testid="beauty-routine-generate"
          >
            {genLoading ? <><Loader2 size={14} className="mr-2 animate-spin" /> Composing</> : <><Sparkles size={14} className="mr-2" /> Suggest reminders</>}
          </Button>
        </section>

        {/* Enabled + suggested */}
        <section className="space-y-8">
          <div>
            <p className="eyebrow flex items-center justify-between">
              <span>Active reminders</span>
              <Link to="/calendar" className="text-[10px] uppercase tracking-[0.2em] underline underline-offset-4 flex items-center gap-1">
                Open calendar <ArrowRight size={11} />
              </Link>
            </p>
            {enabledLoading && <div className="mt-4 p-6 border border-dashed border-border text-center"><Loader2 size={18} className="animate-spin mx-auto" /></div>}
            {!enabledLoading && enabled.length === 0 && (
              <p className="text-sm text-muted-foreground mt-3">No reminders active yet — enable any below to get started.</p>
            )}
            {enabled.length > 0 && (
              <ul className="mt-4 space-y-2" data-testid="beauty-routine-active">
                {enabled.map(r => (
                  <li key={r.id} className="flex items-center gap-4 border border-border px-4 h-14">
                    <Check size={16} className="text-accent shrink-0" />
                    <div className="min-w-0 flex-1">
                      <p className="text-sm truncate"><strong>{r.type}</strong>{r.time_of_day ? ` · ${r.time_of_day}` : ""}</p>
                      <p className="text-xs text-muted-foreground">{r.cadence}{r.note ? ` — ${r.note}` : ""}</p>
                    </div>
                    <button
                      type="button"
                      onClick={() => disable(r.id)}
                      disabled={saving}
                      data-testid={`beauty-routine-disable-${r.id}`}
                      className="text-muted-foreground hover:text-destructive"
                      aria-label="Remove reminder"
                    >
                      <Trash2 size={16} />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {suggested.length > 0 && (
            <div>
              <p className="eyebrow">Suggested from your scan</p>
              <ul className="mt-4 space-y-2" data-testid="beauty-routine-suggested">
                {suggested.map((r, i) => {
                  const already = isEnabled(r.type, r.cadence);
                  return (
                    <li key={i} className="flex items-center gap-4 border border-border px-4 h-14">
                      <div className="min-w-0 flex-1">
                        <p className="text-sm truncate"><strong>{r.type}</strong>{r.time_of_day ? ` · ${r.time_of_day}` : ""}</p>
                        <p className="text-xs text-muted-foreground">{r.cadence}{r.note ? ` — ${r.note}` : ""}</p>
                      </div>
                      <Button
                        variant={already ? "secondary" : "default"}
                        disabled={saving || already}
                        onClick={() => enable(r)}
                        data-testid={`beauty-routine-enable-${i}`}
                        className="rounded-none h-9 text-[10px] uppercase tracking-[0.18em]"
                      >
                        {already ? "Enabled" : "Enable"}
                      </Button>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
